import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isTransactable, listAccounts } from '@/api/accounts'
import { listContacts } from '@/api/people'
import {
  createPersonalLoan,
  type InterestBasis,
  type LoanDirection,
  type CreatePersonalLoanPayload,
} from '@/api/personalLoans'
import { AddPersonInline } from '@/components/owed/AddPersonInline'
import { DatePicker } from '@/components/DatePicker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Radio } from '@/components/ui/radio'
import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fromPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { MODAL_BOX, MODAL_OVERLAY } from '@/components/transactions/TransactionForms'
import {
  LABEL_CLS,
  errorDetail,
  invalidateAfterLoanWrite,
  parsePaise,
  warnIfEarmarkShort,
} from './shared'
import { InterestFields } from './InterestFields'
import { defaultLoanName, rateError } from '@/lib/personalLoans'

export function PersonalLoanFormModal({
  initialDirection = 'LENT',
  initialContactId,
  onClose,
}: {
  initialDirection?: LoanDirection
  /** Set when opened from a person's page. */
  initialContactId?: string
  onClose: () => void
}) {
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [direction, setDirection] = useState<LoanDirection>(initialDirection)
  const [contactId, setContactId] = useState(initialContactId ?? '')
  const [amountText, setAmountText] = useState('')
  const [date, setDate] = useState(today())
  const [accountId, setAccountId] = useState('')
  const [alreadyOwed, setAlreadyOwed] = useState(false)
  const [basis, setBasis] = useState<InterestBasis>('NONE')
  const [rateText, setRateText] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [nameText, setNameText] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [amountError, setAmountError] = useState<string | null>(null)
  const [rateErr, setRateErr] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: false }],
    queryFn: () => listContacts(),
  })
  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const accounts = (accountsData?.accounts ?? []).filter((a) => isTransactable(a.type))

  const person = contacts.find((c) => c.id === contactId)
  const autoName = person ? defaultLoanName(direction, person.name, date) : ''
  const name = nameText ?? autoName

  const mutation = useMutation({
    mutationFn: (payload: CreatePersonalLoanPayload) => createPersonalLoan(payload),
    onSuccess: (data) => {
      invalidateAfterLoanWrite(queryClient)
      warnIfEarmarkShort(data?.earmarkWarning, format, navigate)
      onClose()
    },
    onError: (err) => setSubmitError(errorDetail(err, 'Something went wrong. Please try again.')),
  })

  function submit() {
    setSubmitError(null)
    const paise = parsePaise(amountText)
    const amountBad = paise === null || paise <= 0
    setAmountError(amountBad ? 'Enter an amount greater than ₹0' : null)
    const rErr = rateError(basis, rateText)
    setRateErr(rErr)
    if (amountBad || rErr) return
    mutation.mutate({
      direction,
      contactId,
      principal: fromPaise(paise),
      date,
      ...(alreadyOwed ? { alreadyOwed: true } : { financialAccountId: accountId }),
      interestBasis: basis,
      ...(basis !== 'NONE' ? { interestRate: Number(rateText.trim()) } : {}),
      ...(dueDate ? { dueDate } : {}),
      ...(nameText !== null && nameText.trim() ? { name: nameText.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    })
  }

  return (
    <div className={MODAL_OVERLAY}>
      <div className={MODAL_BOX} role="dialog" aria-label="New loan">
        <h2 className="mb-4 text-lg font-bold text-[var(--fl-text-1)]">New loan</h2>
        {initialContactId && (
          <p className="-mt-2 mb-4 text-xs text-[var(--fl-text-2)]">
            A loan tracks its repayments and can have interest and a due date. For quick money with
            no interest, use Lent money / Borrowed money instead.
          </p>
        )}
        <div className="space-y-4">
          <fieldset className="flex gap-6">
            <legend className={LABEL_CLS}>Direction</legend>
            {(
              [
                ['LENT', 'I lent'],
                ['BORROWED', 'I borrowed'],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]"
              >
                <Radio
                  name="loan-direction"
                  value={value}
                  checked={direction === value}
                  onChange={() => setDirection(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <div>
            <label className={LABEL_CLS}>Person</label>
            <Select aria-label="Person" value={contactId} onChange={setContactId}>
              <option value="">Select person</option>
              {contacts
                .filter((c) => !c.archived)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </Select>
            {!initialContactId && <AddPersonInline onAdded={(c) => setContactId(c.id)} />}
          </div>
          <div>
            <label htmlFor="loan-name" className={LABEL_CLS}>
              Name
            </label>
            <Input
              id="loan-name"
              value={name}
              maxLength={50}
              onChange={(e) => setNameText(e.target.value)}
            />
            {nameText === null && (
              <p className="mt-1 text-xs text-[var(--fl-text-2)]">Auto-named — you can change it</p>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm text-[var(--fl-text-1)]">
            <Checkbox checked={alreadyOwed} onChange={(e) => setAlreadyOwed(e.target.checked)} />
            <span>Already owed before I started using LogMySpends</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="loan-amount" className={LABEL_CLS}>
                {alreadyOwed ? 'Amount still outstanding' : 'Amount'}
              </label>
              <Input
                id="loan-amount"
                inputMode="decimal"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
              />
              {amountError && <p className="mt-1 text-xs text-[var(--fl-error)]">{amountError}</p>}
            </div>
            <div>
              <label htmlFor="loan-date" className={LABEL_CLS}>
                Date
              </label>
              <DatePicker id="loan-date" value={date} onChange={setDate} />
            </div>
          </div>
          {alreadyOwed ? (
            <p className="text-xs text-[var(--fl-text-2)]">
              No account is used. The loan starts with this amount outstanding.
            </p>
          ) : (
            <div>
              <label className={LABEL_CLS}>
                {direction === 'LENT' ? 'Paid from' : 'Received into'}
              </label>
              <Select aria-label="Account" value={accountId} onChange={setAccountId}>
                <option value="">Select account</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <InterestFields
            basis={basis}
            rateText={rateText}
            onBasisChange={setBasis}
            onRateChange={setRateText}
            rateError={rateErr}
          />
          <div>
            <label htmlFor="loan-due-date" className={LABEL_CLS}>
              Due date (optional)
            </label>
            <DatePicker id="loan-due-date" value={dueDate} onChange={setDueDate} />
          </div>
          <div>
            <label htmlFor="loan-notes" className={LABEL_CLS}>
              Reason / notes
            </label>
            <Textarea
              id="loan-notes"
              rows={2}
              maxLength={500}
              placeholder="e.g. Car down payment, to be returned by Diwali"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {submitError && <p className="text-xs text-[var(--fl-error)]">{submitError}</p>}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={
                contactId === '' || (!alreadyOwed && accountId === '') || mutation.isPending
              }
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Saving…' : 'Save loan'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
